import { ckmeans, min } from "simple-statistics";
import { isEmpty, isArray, orderBy, first } from "lodash";

export const DATA_SOURCES = Object.freeze({
  LBS: "lbs",
  CV: "cv"
});

export const DATA_SOURCE_LABELS = Object.freeze({
  [DATA_SOURCES.LBS]: "People",
  [DATA_SOURCES.CV]: "Vehicles"
});

const toArray = (value) => (isArray(value) ? value : [value]);
const round = (value) => Math.round(value);
const pairKey = (from, to) => `${from}\u0000${to}`;

/**
 * Human readable label for an hour aggregation bucket. Studies whose buckets are the raw
 * two digit hours ("00".."23") are shown in 12 hour form ("12 AM".."11 PM"); other labels
 * (e.g. "AM Peak") are returned unchanged.
 */
export function formatHourAgg(label) {
  const text = String(label ?? "").trim();
  if (!/^\d{1,2}$/.test(text) || Number(text) > 23) {
    return label;
  }
  const hour = Number(text);
  return `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? "AM" : "PM"}`;
}

/**
 * Order aggregate rows by the study's bucket order (the order the filter options are listed in);
 * rows for buckets not in the order keep their relative position at the end.
 * @param {Array<Object>} rows
 * @param {String} key   row property holding the bucket label
 * @param {Array<String>} order
 */
export function sortByBucketOrder(rows, key, order = []) {
  const rank = (row) => {
    const index = order.indexOf(row[key]);
    return index === -1 ? order.length : index;
  };
  return [...(rows || [])].sort((a, b) => rank(a) - rank(b));
}

/**
 * Expand the compact CV materialized view (see api scripts/tm-build-cv-view.js)
 * into flat records shaped like the LBS matrix rows.
 */
export function decodeCvView(view) {
  if (isEmpty(view) || isEmpty(view.rows)) {
    return [];
  }
  const { months = [], dayAggs = [], hourAggs = [], zones = [], columns = [], rows } = view;
  const col = (name, fallback) => {
    const index = columns.indexOf(name);
    return index === -1 ? fallback : index;
  };
  const monthCol = col("month", 0);
  const fromCol = col("from", 1);
  const toCol = col("to", 2);
  const dayCol = col("dayAgg", 3);
  const hourCol = col("hourAgg", 4);
  const totalCol = col("total", 5);
  return rows.map((row) => ({
    month: months[row[monthCol]],
    origin_id: String(zones[row[fromCol]]),
    destination_id: String(zones[row[toCol]]),
    day_agg: dayAggs[row[dayCol]],
    hour_agg: hourAggs[row[hourCol]],
    total: +row[totalCol]
  }));
}

export function filterCvRecords(records, { month, dayAgg = [], hourAgg = [] } = {}) {
  const days = new Set(toArray(dayAgg));
  const hours = new Set(toArray(hourAgg));
  return records.filter((r) => (!month || r.month === month) && days.has(r.day_agg) && hours.has(r.hour_agg));
}

/**
 * Inbound + outbound trip totals per zone.
 * @return {Map<String, Number>}
 */
export function zoneTotals(records) {
  const totals = new Map();
  for (const r of records) {
    totals.set(r.origin_id, (totals.get(r.origin_id) || 0) + r.total);
    totals.set(r.destination_id, (totals.get(r.destination_id) || 0) + r.total);
  }
  for (const [zone, total] of totals) {
    totals.set(zone, round(total));
  }
  return totals;
}

/**
 * Natural-break thresholds matching the LBS getThresholds query: up to eight
 * ckmeans clusters, each lower bound floored to the nearest hundred.
 */
export function computeThresholds(values) {
  const data = values.filter((v) => Number.isFinite(v));
  if (isEmpty(data)) {
    return [];
  }
  return ckmeans(data, min([data.length, 8])).map((cluster) => Math.floor(first(cluster) / 100) * 100);
}

export function zoneNamesFromFeatures(zones, { id, name }) {
  const names = {};
  for (const feature of zones?.features || []) {
    names[String(feature.properties[id])] = feature.properties[name];
  }
  return names;
}

export function computeMatrix(records, zoneNames = {}) {
  const pairs = new Map();
  for (const r of records) {
    const key = pairKey(r.origin_id, r.destination_id);
    const pair = pairs.get(key);
    if (pair) {
      pair.total += r.total;
    } else {
      pairs.set(key, {
        origin_id: r.origin_id,
        origin_name: zoneNames[r.origin_id] ?? null,
        destination_id: r.destination_id,
        destination_name: zoneNames[r.destination_id] ?? null,
        total: r.total
      });
    }
  }
  return Array.from(pairs.values(), (pair) => ({ ...pair, total: round(pair.total) }));
}

function totalsBy(records, key, order) {
  const totals = new Map();
  for (const r of records) {
    totals.set(r[key], (totals.get(r[key]) || 0) + r.total);
  }
  const labels = isEmpty(order) ? Array.from(totals.keys()) : order.filter((label) => totals.has(label));
  return labels.map((label) => ({ [key]: label, total: round(totals.get(label)) }));
}

export function tripsByDayAgg(records, order = []) {
  return totalsBy(records, "day_agg", order);
}

export function tripsByHourAgg(records, order = []) {
  return totalsBy(records, "hour_agg", order);
}

/**
 * Top-N matrix matching the LBS getTopMatrix query: the busiest study zones
 * are ranked on all of the study's data, then the cells are filled from the
 * filtered records. Zones without a polygon (not in zoneNames) are skipped.
 */
export function computeTopMatrix(allRecords, filteredRecords, zoneNames = {}, limit = 20) {
  const inbound = new Map();
  const outbound = new Map();
  for (const r of allRecords) {
    outbound.set(r.origin_id, (outbound.get(r.origin_id) || 0) + r.total);
    inbound.set(r.destination_id, (inbound.get(r.destination_id) || 0) + r.total);
  }
  const ranked = [];
  for (const [zone, out] of outbound) {
    if (inbound.has(zone) && zone in zoneNames) {
      ranked.push({ zone, total: round(out) + round(inbound.get(zone)) });
    }
  }
  const topZones = orderBy(ranked, "total", "desc").slice(0, limit).map((z) => z.zone);
  if (isEmpty(topZones)) {
    return [];
  }

  const topSet = new Set(topZones);
  const counts = new Map();
  for (const r of filteredRecords) {
    if (topSet.has(r.origin_id) && topSet.has(r.destination_id)) {
      const key = pairKey(r.origin_id, r.destination_id);
      counts.set(key, (counts.get(key) || 0) + r.total);
    }
  }

  const cells = [];
  for (const from of topZones) {
    for (const to of topZones) {
      cells.push({
        origin_name: zoneNames[from] ?? null,
        destination_name: zoneNames[to] ?? null,
        total: round(counts.get(pairKey(from, to)) || 0)
      });
    }
  }
  return orderBy(cells, ["origin_name", "destination_name"], ["desc", "asc"]);
}

/**
 * Zones GeoJSON decorated with the per-zone total so it can drive the same
 * step-colored fill layer the LBS vector tiles use.
 */
export function buildChoropleth(zones, totals, idKey) {
  return {
    type: "FeatureCollection",
    features: (zones?.features || []).map((feature) => {
      const zoneId = String(feature.properties[idKey]);
      return {
        type: "Feature",
        geometry: feature.geometry,
        properties: {
          zone_id: zoneId,
          total: totals.get(zoneId) || 0
        }
      };
    })
  };
}
